import { defineMessages, type MessageDescriptor } from 'react-intl';
import type { POISubType } from '@alltrails/types/AlgoliaResults';

const labels = defineMessages({
  peak: { defaultMessage: 'Peaks' },
  waterfall: { defaultMessage: 'Waterfalls' },
  garden: { defaultMessage: 'Gardens' },
  birdHide: { defaultMessage: 'Bird blinds' },
  wildlifeHide: { defaultMessage: 'Wildlife blinds' },
  beach: { defaultMessage: 'Beaches' },
  lake: { defaultMessage: 'Lakes' },
  hotSpring: { defaultMessage: 'Hot springs' },
  viewpoint: { defaultMessage: 'Viewpoints' },
  river: { defaultMessage: 'Rivers' },
  cave: { defaultMessage: 'Caves' },
  historicSite: { defaultMessage: 'Historic sites' },
  campsite: { defaultMessage: 'Campsites' },
  other: { defaultMessage: 'Other' }
});

// Follows getPOIIcon, so subtypes sharing a pin share a checkbox
export const poiGroups = [
  { id: 'peak', label: labels.peak, subtypes: ['peak'] },
  { id: 'waterfall', label: labels.waterfall, subtypes: ['waterfall'] },
  { id: 'garden', label: labels.garden, subtypes: ['garden'] },
  { id: 'birdHide', label: labels.birdHide, subtypes: ['bird_hide'] },
  { id: 'wildlifeHide', label: labels.wildlifeHide, subtypes: ['wildlife_hide'] },
  { id: 'beach', label: labels.beach, subtypes: ['bay', 'beach'] },
  { id: 'lake', label: labels.lake, subtypes: ['lake'] },
  { id: 'hotSpring', label: labels.hotSpring, subtypes: ['hot_spring'] },
  { id: 'viewpoint', label: labels.viewpoint, subtypes: ['viewpoint'] },
  { id: 'river', label: labels.river, subtypes: ['river'] },
  { id: 'cave', label: labels.cave, subtypes: ['cave'] },
  { id: 'historicSite', label: labels.historicSite, subtypes: ['archaeological_site', 'historic_site', 'ruins', 'monument'] },
  { id: 'campsite', label: labels.campsite, subtypes: ['campsite'] },
  { id: 'other', label: labels.other, subtypes: [] }
] as const satisfies readonly { id: string; label: MessageDescriptor; subtypes: readonly POISubType[] }[];

/** Persisted to localStorage, so renaming an id drops that exclusion for every user. */
export type PoiGroupId = (typeof poiGroups)[number]['id'];

const subtypeToGroupId = new Map<POISubType, PoiGroupId>(
  poiGroups.flatMap(group => group.subtypes.map((subtype): [POISubType, PoiGroupId] => [subtype, group.id]))
);

export const getPoiGroupId = (subtype: POISubType): PoiGroupId => subtypeToGroupId.get(subtype) ?? 'other';

export const isPoiGroupId = (value: unknown): value is PoiGroupId => poiGroups.some(group => group.id === value);
